import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import Seo from "../components/Seo";

export default function Contact() {
  const recaptchaRef = useRef(null);
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    website: "",
  });

  const [captchaToken, setCaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formStartedAt, setFormStartedAt] = useState(Date.now());

  useEffect(() => {
    setFormStartedAt(Date.now());
  }, []);

  function handleChange(e) {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.name.trim() || !form.phone.trim()) {
      alert("Συμπληρώστε τα υποχρεωτικά πεδία.");
      return;
    }

    if (!siteKey) {
      alert("Το reCAPTCHA δεν έχει ρυθμιστεί σωστά.");
      return;
    }

    if (!captchaToken) {
      alert("Παρακαλώ επιβεβαιώστε ότι δεν είστε bot.");
      return;
    }

    try {
      setSubmitting(true);

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          captchaToken,
          formStartedAt,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Αποτυχία αποστολής.");
      }

      alert(data?.message || "Το μήνυμά σας στάλθηκε.");

      setForm({
        name: "",
        phone: "",
        email: "",
        message: "",
        website: "",
      });

      setCaptchaToken("");
      setFormStartedAt(Date.now());
      recaptchaRef.current?.reset();
    } catch (err) {
      alert(err.message || "Κάτι πήγε στραβά.");
    } finally {
      setSubmitting(false);
    }
  }

return (
  <>
    <Seo
      title="Επικοινωνία – ΚΔΒΜ2 ΘΗΣΕΑΣ"
      description="Επικοινωνήστε με το ΚΔΒΜ2 ΘΗΣΕΑΣ για πληροφορίες σχετικά με προγράμματα κατάρτισης και voucher ΔΥΠΑ."
      keywords="επικοινωνία ΘΗΣΕΑΣ, πληροφορίες κατάρτιση, voucher ΔΥΠΑ"
      url="https://theseus.edu.gr/contact"
      image="/theseus.png"
    />

    <section className="min-h-[80vh] flex items-center justify-center bg-white px-4 py-16">
      <div className="w-full max-w-xl text-center">
        <img
          src={`${process.env.PUBLIC_URL}/theseus.png`}
          alt="ΘΗΣΕΑΣ"
          className="mx-auto h-16 mb-6"
        />

        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
          Επικοινωνία
        </h1>

        <p className="mt-3 text-gray-600 text-lg">
          Συμπληρώστε τη φόρμα και θα επικοινωνήσουμε μαζί σας το συντομότερο.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5 text-left">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Ονοματεπώνυμο *
            </label>
            <input
              type="text"
              name="name"
              required
              value={form.name}
              onChange={handleChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Τηλέφωνο *
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={form.phone}
              onChange={handleChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Μήνυμα
            </label>
            <textarea
              name="message"
              rows={4}
              value={form.message}
              onChange={handleChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="hidden" aria-hidden="true">
            <label>
              Website
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={form.website}
                onChange={handleChange}
              />
            </label>
          </div>

          <div className="flex justify-center min-h-[78px]">
            {siteKey ? (
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={siteKey}
                onChange={(token) => setCaptchaToken(token || "")}
              />
            ) : (
              <div className="text-sm text-red-600 text-center">
                Το reCAPTCHA site key λείπει από το build.
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || !siteKey}
            className="w-full rounded-xl bg-blue-600 px-6 py-3 text-white font-semibold hover:bg-blue-700 transition disabled:opacity-60"
          >
            {submitting ? "Αποστολή..." : "Αποστολή Μηνύματος"}
          </button>
        </form>
      </div>
    </section>
  </>
);
}